import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Users, Phone, Mail, Building, Calendar, TrendingUp } from 'lucide-react';

const ClientOverview = ({ clients }) => {
  const [expandedSections, setExpandedSections] = useState({
    byType: true,
    daily: false,
    weekly: false,
    monthly: false
  });
  const [selectedClient, setSelectedClient] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  // Client type labels in Indonesian
  const clientTypeLabels = {
    individual: '👤 Personal/Perorangan',
    corporate: '🏢 Perusahaan',
    event_organizer: '🎉 Event Organizer',
    education: '🎓 Pendidikan',
    university: '🏫 Universitas',
    office: '🏛️ Kantor',
    business_entity: '🏭 Badan Usaha',
    cv: '📋 CV (Commanditaire Vennootschap)',
    business_unit: '🏬 Unit Usaha'
  };

  // Group clients by type
  const clientsByType = useMemo(() => {
    const grouped = {};
    
    Object.keys(clientTypeLabels).forEach(type => {
      const clientsOfType = clients.filter(c => c.client_type === type);
      if (clientsOfType.length > 0) {
        grouped[type] = {
          label: clientTypeLabels[type],
          clients: clientsOfType,
          count: clientsOfType.length,
          activeCount: clientsOfType.filter(c => c.is_active !== false).length
        };
      }
    });
    
    return grouped;
  }, [clients]);

  // Group clients by last update time
  const getTimeBasedGroups = (timeFrame) => {
    const now = new Date();
    const grouped = {};

    clients.forEach(client => {
      const createdAt = new Date(client.created_at);
      const diffTime = Math.abs(now - createdAt);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      let inRange = false;
      if (timeFrame === 'daily' && diffDays <= 1) inRange = true;
      if (timeFrame === 'weekly' && diffDays <= 7) inRange = true;
      if (timeFrame === 'monthly' && diffDays <= 30) inRange = true;

      if (inRange) {
        const type = client.client_type;
        if (!grouped[type]) {
          grouped[type] = {
            label: clientTypeLabels[type] || type,
            clients: [],
            count: 0
          };
        }
        grouped[type].clients.push(client);
        grouped[type].count++;
      }
    });

    return grouped;
  };

  const dailyGroups = useMemo(() => getTimeBasedGroups('daily'), [clients]);
  const weeklyGroups = useMemo(() => getTimeBasedGroups('weekly'), [clients]);
  const monthlyGroups = useMemo(() => getTimeBasedGroups('monthly'), [clients]);

  const handleClientClick = (client) => {
    setSelectedClient(client);
    setShowDetailModal(true);
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '-';
    // Format: 0857-4700-1236
    return phone.replace(/(\d{4})(\d{4})(\d{4})/, '$1-$2-$3');
  };

  const ClientRow = ({ client, showType = false }) => (
    <div 
      onClick={() => handleClientClick(client)}
      className="flex items-center justify-between py-3 px-4 hover:bg-primary/5 rounded-lg transition-colors cursor-pointer border border-transparent hover:border-primary/20"
    >
      <div className="flex-1">
        <p className="font-medium text-foreground">
          {client.client_type === 'individual' ? client.full_name : client.company_name}
        </p>
        {showType && (
          <p className="text-xs text-muted-foreground capitalize">
            {clientTypeLabels[client.client_type]?.replace(/[^\w\s]/g, '').trim()}
          </p>
        )}
        <p className="text-xs text-muted-foreground">{client.email}</p>
      </div>
      <div className="flex items-center space-x-3">
        <div className="text-right hidden sm:block">
          <p className="text-sm font-medium text-foreground flex items-center">
            <Phone className="w-3 h-3 mr-1" />
            {formatPhoneNumber(client.phone)}
          </p>
          <p className="text-xs text-muted-foreground">
            {client.total_rentals || 0} rentals
          </p>
        </div>
        <div className={`px-2 py-1 rounded text-xs font-medium ${
          client.is_active !== false ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'
        }`}>
          {client.is_active !== false ? 'Active' : 'Inactive'}
        </div>
      </div>
    </div>
  );

  const TypeSection = ({ typeKey, data }) => (
    <div className="bg-muted/30 rounded-lg overflow-hidden">
      <div className="p-4 bg-muted/50 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">{data.label.split(' ')[0]}</span>
          <div>
            <h4 className="font-semibold text-foreground">{data.label.substring(2)}</h4>
            <p className="text-xs text-muted-foreground">
              {data.count} total • {data.activeCount || data.count} active
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-primary">{data.count}</p>
          <p className="text-xs text-muted-foreground">clients</p>
        </div>
      </div>
      <div className="p-2 space-y-1 max-h-60 overflow-y-auto">
        {data.clients.slice(0, 5).map((client, idx) => (
          <ClientRow key={idx} client={client} />
        ))}
        {data.clients.length > 5 && (
          <p className="text-xs text-center text-muted-foreground py-2">
            +{data.clients.length - 5} more clients (click section to see all)
          </p>
        )}
      </div>
    </div>
  );

  const Section = ({ title, data, sectionKey, icon, emptyMessage }) => {
    const totalClients = Object.values(data).reduce((sum, group) => sum + group.count, 0);
    
    return (
      <div className="glass-card rounded-xl border border-border/40 overflow-hidden">
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
        >
          <div className="flex items-center space-x-3">
            {icon}
            <div className="text-left">
              <h3 className="text-lg font-semibold text-foreground">{title}</h3>
              <p className="text-xs text-muted-foreground">
                {totalClients} clients total
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
              {totalClients}
            </span>
            {expandedSections[sectionKey] ? (
              <ChevronUp className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
        </button>
        {expandedSections[sectionKey] && (
          <div className="p-4 pt-0 space-y-3 border-t border-border">
            {Object.keys(data).length > 0 ? (
              Object.keys(data).map((key) => (
                <TypeSection key={key} typeKey={key} data={data[key]} />
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">{emptyMessage}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4 mb-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-foreground flex items-center">
            <Users className="w-6 h-6 mr-2 text-primary" />
            Client Overview - Real-Time Data
          </h2>
          <p className="text-sm text-muted-foreground">
            Total: {clients.length} clients | Active: {clients.filter(c => c.is_active !== false).length}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-xs text-muted-foreground">Last updated:</span>
          <span className="text-xs font-medium text-foreground">{new Date().toLocaleString('id-ID')}</span>
        </div>
      </div>

      {/* Main Overview by Type */}
      <Section
        title="📊 Semua Client (Per Kategori)"
        data={clientsByType}
        sectionKey="byType"
        icon={<Building className="w-6 h-6 text-blue-500" />}
        emptyMessage="Belum ada client terdaftar"
      />

      {/* Daily Updates */}
      <Section
        title="📅 Update Harian (24 Jam Terakhir)"
        data={dailyGroups}
        sectionKey="daily"
        icon={<Calendar className="w-6 h-6 text-green-500" />}
        emptyMessage="Tidak ada client baru dalam 24 jam terakhir"
      />

      {/* Weekly Updates */}
      <Section
        title="📆 Update Mingguan (7 Hari Terakhir)"
        data={weeklyGroups}
        sectionKey="weekly"
        icon={<TrendingUp className="w-6 h-6 text-yellow-500" />}
        emptyMessage="Tidak ada client baru dalam 7 hari terakhir"
      />

      {/* Monthly Updates */}
      <Section
        title="📈 Update Bulanan (30 Hari Terakhir)"
        data={monthlyGroups}
        sectionKey="monthly"
        icon={<TrendingUp className="w-6 h-6 text-purple-500" />}
        emptyMessage="Tidak ada client baru dalam 30 hari terakhir"
      />

      {/* Detail Modal */}
      {showDetailModal && selectedClient && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-card p-6 rounded-xl border border-border/40 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-foreground">Detail Client</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">
              {/* Client Type Badge */}
              <div className="flex items-center space-x-3">
                <span className="text-3xl">
                  {clientTypeLabels[selectedClient.client_type]?.split(' ')[0]}
                </span>
                <div>
                  <p className="text-sm text-muted-foreground">Kategori Client</p>
                  <p className="font-semibold text-foreground">
                    {clientTypeLabels[selectedClient.client_type]?.substring(2)}
                  </p>
                </div>
              </div>

              {/* Main Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">
                      {selectedClient.client_type === 'individual' ? 'Nama Lengkap' : 'Nama Perusahaan'}
                    </p>
                    <p className="text-lg font-semibold text-foreground">
                      {selectedClient.client_type === 'individual' 
                        ? selectedClient.full_name 
                        : selectedClient.company_name}
                    </p>
                  </div>

                  {selectedClient.contact_person && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Contact Person</p>
                      <p className="font-medium text-foreground">{selectedClient.contact_person}</p>
                      {selectedClient.contact_position && (
                        <p className="text-sm text-muted-foreground">{selectedClient.contact_position}</p>
                      )}
                    </div>
                  )}

                  {selectedClient.id_number && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">No. Identitas</p>
                      <p className="font-mono text-foreground">{selectedClient.id_number}</p>
                    </div>
                  )}

                  {selectedClient.tax_id && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">NPWP</p>
                      <p className="font-mono text-foreground">{selectedClient.tax_id}</p>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {/* Contact Info - Highlighted */}
                  <div className="bg-primary/10 p-4 rounded-lg border border-primary/30">
                    <p className="text-sm text-muted-foreground mb-3 font-semibold">📞 Kontak</p>
                    <div className="space-y-2">
                      <a 
                        href={`tel:${selectedClient.phone}`}
                        className="flex items-center space-x-2 text-foreground hover:text-primary transition-colors"
                      >
                        <Phone className="w-4 h-4" />
                        <span className="font-medium">{formatPhoneNumber(selectedClient.phone)}</span>
                      </a>
                      <a 
                        href={`mailto:${selectedClient.email}`}
                        className="flex items-center space-x-2 text-foreground hover:text-primary transition-colors"
                      >
                        <Mail className="w-4 h-4" />
                        <span className="text-sm">{selectedClient.email}</span>
                      </a>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Alamat</p>
                    <p className="text-foreground">{selectedClient.address || '-'}</p>
                    {selectedClient.company_address && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {selectedClient.company_address}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Business Info */}
              <div className="border-t border-border pt-4">
                <h4 className="font-semibold text-foreground mb-3">Informasi Transaksi</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-muted/30 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground">Total Rentals</p>
                    <p className="text-xl font-bold text-foreground">{selectedClient.total_rentals || 0}</p>
                  </div>
                  <div className="bg-muted/30 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground">Total Paid</p>
                    <p className="text-lg font-bold text-green-600">
                      {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(selectedClient.total_paid || 0)}
                    </p>
                  </div>
                  <div className="bg-muted/30 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground">Outstanding</p>
                    <p className="text-lg font-bold text-orange-600">
                      {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(selectedClient.outstanding_balance || 0)}
                    </p>
                  </div>
                  <div className="bg-muted/30 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground">Payment Terms</p>
                    <p className="text-xl font-bold text-foreground">{selectedClient.payment_terms || 30} hari</p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedClient.notes && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Catatan</p>
                  <p className="text-foreground bg-muted/30 p-3 rounded-lg">{selectedClient.notes}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
                <a
                  href={`tel:${selectedClient.phone}`}
                  className="flex-1 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <Phone className="w-4 h-4" />
                  <span>Call Now</span>
                </a>
                <a
                  href={`https://wa.me/${selectedClient.phone.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <span>💬</span>
                  <span>WhatsApp</span>
                </a>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="flex-1 px-4 py-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="glass-card p-4 rounded-xl border border-blue-500/40 bg-blue-500/5">
        <p className="text-sm text-foreground">
          💡 <strong>Tips:</strong> Klik pada nama client untuk melihat detail lengkap dan nomor kontak.
          Data diupdate secara real-time dari database!
        </p>
      </div>
    </div>
  );
};

export default ClientOverview;
